/**
 * 항목 수정 모달 → SMS 발송 팝업 문안 빌더 + 팝업 오픈 (Alpine magic `$sms`, 2026-09-16).
 *
 * 레거시 lawdesk 의 각 수정 팝업 `ico_sms` 핸들러(regTab0102mod01~03 · regTap0101mod01~02 · regTap0301 · editSugum) 문안을 미러한다.
 *   - 진행내역: 2023.11.17 간결형(라벨 없이 줄만) — 사건 식별 / 유형(+기일관리) / 일자 / 내용 / 결과
 *   - 업무·비용·수금·문서·타임시트: '= 제목 =' + '라벨:값' 줄 (문서·타임시트는 docModify/editTimeSheet 미러, 2026-10-13)
 * 전달은 실무계산기(helper/calc0*)와 동일하게 GET `/sms/popup?txtParam=` 새 창(레거시는 POST 였음).
 * 수신자는 레거시처럼 넘기지 않는다 — SMS 창의 주소록에서 선택.
 *   - 예외: 소송 상세 의뢰인/상대방 탭의 번호 옆 아이콘은 `$sms.openTo(이름, 번호)` 로 수신자만 채워 연다(문안 없음, 2026-10-06).
 *
 * 사용: `$sms.open($sms.work({...}))` 처럼 빌더 결과를 open 에 넘긴다.
 */
const WORK_STATUS  = { '1': '진행', '2': '대기', '3': '보류', '4': '취소', '5': '완료', '9': '기타' };
const GIGIL_MANAGE = { '1': '참석(예정)', '2': '불참(예정)', '3': '기일변경(예정)', '4': '복대리(예정)', '5': '무시' };
const SUGUM_STATUS = { '1': '미통지', '2': '입금대기', '3': '입금완료', '4': '과소입금', '5': '포기/감면', '6': '청구불필요' };
const DOC_GUBUN    = { '1': '수신', '2': '발신', '3': '보관', '9': '기타' }; // 문서 모달 select 와 동일(2026-10-13)

const s = v => (v == null ? '' : String(v)).trim();
const dot = d => s(d).replace(/-/g, '.');
const nonEmpty = lines => lines.filter(l => l != null && l !== '');

/** SMS 발송 팝업 — header.html·calc0* 와 동일 창 이름/크기(같은 이름이라 재호출 시 내용만 갱신). */
function popup(query) {
    const w = 900, h = 800;
    const t = Math.max(0, (screen.availHeight - h) / 2);
    const l = Math.max(0, (screen.availWidth - w) / 2);
    window.open('/sms/popup?' + query, 'smsPopupWindow',
        `width=${w},height=${h},top=${t},left=${l},scrollbars=no,resizable=no`);
}

export function open(text) {
    popup('txtParam=' + encodeURIComponent(text));
}

/** 휴대전화 여부 — smsForm.parseInput · PhoneNormalizer.MOBILE 과 같은 식(SMS 창에서 유효 칩이 되는 번호만 true). */
export function isMobile(v) {
    return /^01[016789]\d{7,8}$/.test(s(v).replace(/[-\s]/g, ''));
}

/** 수신자 지정 오픈 — receiverParam '이름|번호' 를 smsPopupForm.init 이 수신자 칩으로 만든다. 이름의 '|'·줄바꿈은 구분자와 충돌해 제거. */
export function openTo(name, phone) {
    const nm = s(name).replace(/[|\r\n]/g, '');
    const ph = s(phone).replace(/[-\s]/g, '');
    popup('receiverParam=' + encodeURIComponent(nm ? nm + '|' + ph : ph));
}

/** 진행내역 — ident(사건 식별) / 유형(+기일관리) / 일자(기일은 시각) / 내용 / 결과(송달: 결과날짜 + 공시송달|결과). */
export function progress(o) {
    const isGigil = s(o.code) === '5';
    const isSongdal = s(o.code) === '2';
    const typeLine = s(o.typeLabel) + (isGigil && GIGIL_MANAGE[s(o.gigilManage)] ? ' · ' + GIGIL_MANAGE[s(o.gigilManage)] : '');
    const dateLine = dot(o.date) + (isGigil && !o.noTime && s(o.time) ? ' ' + s(o.time) : '');
    let resultLine = '';
    if (isSongdal && s(o.resultDate)) {
        resultLine = dot(o.resultDate) + (o.gongsi ? ' 0시 도달(공시송달)' : (s(o.result) ? ' ' + s(o.result) : ''));
    } else if (s(o.result)) {
        resultLine = s(o.result);
    }
    return nonEmpty([s(o.ident), typeLine, dateLine, s(o.content), resultLine]).join('\n');
}

/** 업무·상담·약속·메모 — 레거시 regTap0101mod '= 진행사항 =' 미러. */
export function work(o) {
    const time = o.noTime ? '없음' : (s(o.time) + (s(o.spentMin) ? ' (경과시간: ' + s(o.spentMin) + '분)' : ''));
    return nonEmpty([
        '= 진행사항 =',
        '유형:' + s(o.typeLabel),
        s(o.ident) ? '연계정보:' + s(o.ident) : '',
        '업무관리상태:' + (WORK_STATUS[s(o.ustatus)] || ''),
        '일자:' + dot(o.date),
        '시간:' + time,
        '내용:',
        s(o.content)
    ]).join('\n');
}

/** 비용 — 레거시 regTap0301 '= 비용 =' 미러(지출만 의뢰인청구 O/X). */
export function cost(o) {
    const refund = s(o.gubun2) === '2';
    const charge = refund ? '' : (o.charge ? ' (의뢰인청구 O)' : ' (의뢰인청구 X)');
    return nonEmpty([
        '= 비용 =',
        '유형:' + (refund ? '환급' : '지출'),
        s(o.ident) ? '연계정보:' + s(o.ident) : '',
        '일자:' + dot(o.date),
        s(o.place) ? (refund ? '환급처:' : '수납처:') + s(o.place) : '',
        '금액:' + s(o.amount) + '원' + charge,
        '내용:',
        s(o.content)
    ]).join('\n');
}

/** 문서 — 레거시 docModify ico_sms '= 문서 =' 미러(2026-10-13). 링크url 은 원문(레거시 encodeURIComponent 는 open 이 전체 문안을 인코딩하므로 중복 — 미적용). */
export function doc(o) {
    return nonEmpty([
        '= 문서 =',
        s(o.ident) ? '연계정보:' + s(o.ident) : '',
        '유형:' + (DOC_GUBUN[s(o.gubun3)] || ''),
        '수발일:' + dot(o.subdate),
        s(o.place) ? '수발처:' + s(o.place) : '',
        '문서명:' + s(o.name),
        s(o.url) ? '링크url:' : '',
        s(o.url)
    ]).join('\n');
}

/** 타임시트 — 레거시 editTimeSheet ico_sms '= 타임시트 =' 미러(2026-10-13). */
export function timesheet(o) {
    return nonEmpty([
        '= 타임시트 =',
        s(o.ident) ? '연계정보:' + s(o.ident) : '',
        '업무수행자:' + s(o.perf),
        '업무일:' + dot(o.date),
        '업무시간:' + s(o.min) + '분',
        '내용:',
        s(o.content)
    ]).join('\n');
}

/** 수금 — 레거시 editSugum '= 수금 =' 미러. */
export function sugum(o) {
    return nonEmpty([
        '= 수금 =',
        s(o.ident) ? '연계정보:' + s(o.ident) : '',
        '수금관리상태:' + (SUGUM_STATUS[s(o.ustatus)] || ''),
        '입금예정일:' + dot(o.planDate),
        s(o.planAmount) ? '예정금액:' + s(o.planAmount) + '원' : '',
        s(o.realDate) ? '실제수금일:' + dot(o.realDate) : '',
        s(o.realAmount) ? '수금금액:' + s(o.realAmount) + '원' : '',
        '내용:',
        s(o.content)
    ]).join('\n');
}
